// Fix WH61 (2026-09-25): la hoja "Dashboard" del Excel de Gabriela (KPIS con
// Dashboard.xlsx) dentro del Tablero de KPIs. Arriba: promedio general, áreas
// evaluadas, áreas que cumplen y áreas en riesgo. Una fila por área con
// Resultado, Meta 100%, Estado y Bono sugerido; gráfica de resultado por área
// contra la meta y dona con la distribución del estado. Los números salen de
// GET /ops/kpis/board/dashboard, que usa el mismo cálculo que el tablero.
// El Excel comparaba los puntos (0-100) contra 96% (0.96) y marcaba "Cumple"
// casi siempre; acá todo está en la misma escala de 0 a 100.
import { useState, useEffect, Fragment } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ChevronRight, ChevronDown } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, Cell, PieChart, Pie,
} from "recharts";

const ESTADOS = {
  cumple: { label: "Cumple", color: "#22c55e", chip: "bg-green-50 text-green-700 border-green-200" },
  parcial: { label: "Parcial", color: "#fbbf24", chip: "bg-amber-50 text-amber-700 border-amber-200" },
  no_cumple: { label: "No cumple", color: "#ef4444", chip: "bg-red-50 text-red-700 border-red-200" },
  sin_datos: { label: "Sin datos", color: "#e2e8f0", chip: "bg-slate-50 text-slate-500 border-slate-200" },
  falta_configurar: { label: "Falta configurar", color: "#94a3b8", chip: "bg-slate-100 text-slate-600 border-slate-300" },
};
const ORDEN_ESTADOS = ["cumple", "parcial", "no_cumple", "sin_datos", "falta_configurar"];

const LECTURA = [
  ["cumple", "resultado igual o mayor a 96%."],
  ["parcial", "resultado de 90% a 95.9%."],
  ["no_cumple", "resultado menor a 90%."],
  ["sin_datos", "todavía no hay resultados en el período."],
  ["falta_configurar", "las ponderaciones del área no suman exactamente 100 o hay KPIs con ponderación y sin meta. No se califica hasta completarlo."],
];

function fmtPct(v) {
  if (v === null || v === undefined) return "—";
  return `${Number(v).toLocaleString("es-MX", { maximumFractionDigits: 1 })}%`;
}

function EstadoChip({ estado }) {
  const e = ESTADOS[estado] || ESTADOS.sin_datos;
  return (
    <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${e.chip}`}>
      {e.label}
    </span>
  );
}

// Qué le falta a un área para poder calificarse.
function faltaTexto(config) {
  if (!config || config.completa) return null;
  if (!config.kpis) return "El área no tiene KPIs";
  const partes = [];
  if (!config.ponderacion_ok) partes.push(`Ponderación ${config.ponderacion} de 100`);
  if (config.kpis_sin_meta > 0) partes.push(`${config.kpis_sin_meta} KPI${config.kpis_sin_meta === 1 ? "" : "s"} sin meta`);
  return partes.join(" · ");
}

function Tarjeta({ titulo, valor, color, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={testid}>
      <div className="px-4 py-1.5 text-[11px] font-bold uppercase tracking-wide text-white" style={{ backgroundColor: color }}>
        {titulo}
      </div>
      <div className="px-4 py-4 text-3xl font-extrabold" style={{ color }}>{valor}</div>
    </div>
  );
}

export default function KpiDashboardGeneral({ period, weekStart, recarga, onAbrirArea }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [abiertas, setAbiertas] = useState({});

  useEffect(() => {
    let vivo = true;
    setLoading(true);
    const params = new URLSearchParams({ period });
    if (weekStart) params.set("week_start", weekStart);
    api.get(`/ops/kpis/board/dashboard?${params}`)
      .then((res) => {
        if (vivo) setData(res.data);
      })
      .catch((e) => {
        if (vivo) {
          toast.error(e?.response?.data?.detail || "No se pudo cargar el dashboard de KPIs");
          setData(null);
        }
      })
      .finally(() => {
        if (vivo) setLoading(false);
      });
    return () => {
      vivo = false;
    };
  }, [period, weekStart, recarga]);

  if (loading) {
    return (
      <div className="h-64 flex items-center justify-center" data-testid="kpi-dashboard-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!data) return null;

  const areas = data.areas || [];
  const conteo = data.conteo_estados || {};
  const barras = areas.map((a) => ({ name: a.category_name, resultado: a.resultado, estado: a.estado }));
  const maxX = Math.max(110, ...areas.map((a) => a.resultado || 0));
  const dona = ORDEN_ESTADOS.filter((e) => (conteo[e] || 0) > 0)
    .map((e) => ({ estado: e, name: ESTADOS[e].label, value: conteo[e] }));
  const toggle = (code) => setAbiertas((p) => ({ ...p, [code]: !p[code] }));

  return (
    <div className="space-y-5" data-testid="kpi-dashboard-general">
      <div>
        <h2 className="text-lg font-bold text-slate-900">Dashboard general de KPIs</h2>
        <p className="text-xs text-slate-500">
          Resultado de cada área con lo que captura la evaluadora y los KPIs automáticos.
          Ventana: {data.window?.from} → {data.window?.to}
        </p>
      </div>

      {/* Tarjetas */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tarjeta titulo="Promedio general" valor={fmtPct(data.promedio_general)} color="#1A1A1A" testid="dash-promedio" />
        <Tarjeta titulo="Áreas evaluadas" valor={`${data.areas_evaluadas} de ${areas.length}`} color="#334155" testid="dash-evaluadas" />
        <Tarjeta titulo="Áreas que cumplen" valor={data.areas_cumplen} color="#16a34a" testid="dash-cumplen" />
        <Tarjeta titulo="Áreas en riesgo" valor={data.areas_en_riesgo} color="#dc2626" testid="dash-riesgo" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-start">
        {/* Tabla por área */}
        <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="dash-tabla">
              <thead>
                <tr className="text-[11px] uppercase text-slate-400 border-b border-slate-100">
                  <th className="py-2 px-3">Área</th>
                  <th className="py-2 px-3 text-right">Resultado</th>
                  <th className="py-2 px-3 text-right">Meta</th>
                  <th className="py-2 px-3">Estado</th>
                  <th className="py-2 px-3 whitespace-nowrap">Bono sugerido</th>
                </tr>
              </thead>
              <tbody>
                {areas.map((a) => {
                  const porSucursal = Array.isArray(a.sucursales);
                  const abierta = !!abiertas[a.category_code];
                  const falta = faltaTexto(a.configuracion);
                  return (
                    <Fragment key={a.category_code}>
                      <tr
                        className="border-b border-slate-100 hover:bg-slate-50/60 cursor-pointer"
                        onClick={() => (porSucursal
                          ? toggle(a.category_code)
                          : onAbrirArea(a.category_code, a.category_name, "corporativo", "Corporativo"))}
                        data-testid={`dash-area-${a.category_code}`}
                      >
                        <td className="py-2 px-3 text-sm font-medium text-slate-800 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1">
                            {porSucursal && (abierta
                              ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                              : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />)}
                            {a.category_name}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-sm font-semibold text-slate-800 text-right">{fmtPct(a.resultado)}</td>
                        <td className="py-2 px-3 text-sm text-slate-500 text-right">{fmtPct(a.meta)}</td>
                        <td className="py-2 px-3">
                          <EstadoChip estado={a.estado} />
                          {falta && <div className="text-[10px] text-slate-400 mt-0.5">{falta}</div>}
                        </td>
                        <td className="py-2 px-3 text-xs text-slate-600 whitespace-nowrap">
                          {porSucursal ? <span className="italic text-slate-400">Por sucursal</span> : (a.bono || "—")}
                        </td>
                      </tr>
                      {porSucursal && abierta && a.sucursales.map((s) => (
                        <tr
                          key={s.branch_id}
                          className="border-b border-slate-100 bg-slate-50/60 hover:bg-slate-100/60 cursor-pointer"
                          onClick={() => onAbrirArea(a.category_code, a.category_name, s.branch_id, s.branch_name)}
                          data-testid={`dash-area-${a.category_code}-${s.branch_id}`}
                        >
                          <td className="py-1.5 pl-9 pr-3 text-xs text-slate-600 whitespace-nowrap">{s.branch_name}</td>
                          <td className="py-1.5 px-3 text-xs font-semibold text-slate-700 text-right">{fmtPct(s.resultado)}</td>
                          <td className="py-1.5 px-3 text-xs text-slate-400 text-right">{fmtPct(a.meta)}</td>
                          <td className="py-1.5 px-3"><EstadoChip estado={s.estado} /></td>
                          <td className="py-1.5 px-3 text-xs text-slate-600 whitespace-nowrap">{s.bono || "—"}</td>
                        </tr>
                      ))}
                    </Fragment>
                  );
                })}
                <tr className="bg-[#1A1A1A] text-white" data-testid="dash-total">
                  <td className="py-2 px-3 text-sm font-bold whitespace-nowrap">Total / promedio</td>
                  <td className="py-2 px-3 text-sm font-bold text-right text-[#FFDF00]">{fmtPct(data.promedio_general)}</td>
                  <td className="py-2 px-3 text-sm text-right">{fmtPct(data.meta)}</td>
                  <td className="py-2 px-3"><EstadoChip estado={data.estado_general} /></td>
                  <td className="py-2 px-3 text-xs">—</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="px-3 py-2 text-[11px] text-slate-400">
            Da clic en un área para ver sus KPIs. Gerente de Sucursal, Caja y Cocina se califican por sucursal.
          </p>
        </div>

        {/* Resultado por área vs meta */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-sm font-bold text-slate-700 mb-2">Resultado por área vs meta</h3>
          {data.areas_evaluadas === 0 ? (
            <div className="h-48 flex items-center justify-center text-sm text-slate-400 text-center" data-testid="dash-grafica-vacia">
              Todavía no hay áreas calificadas en este período.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(260, barras.length * 32 + 40)}>
              <BarChart data={barras} layout="vertical" margin={{ left: 10, right: 20, top: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" domain={[0, maxX]} ticks={[0, 25, 50, 75, 100]} tick={{ fontSize: 11 }} unit="%" />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={150} />
                <Tooltip formatter={(v) => [fmtPct(v), "Resultado"]} />
                <ReferenceLine x={100} stroke="#1A1A1A" strokeDasharray="4 4" label={{ value: "Meta 100%", position: "top", fontSize: 11 }} />
                <Bar dataKey="resultado" name="Resultado" radius={[0, 4, 4, 0]}>
                  {barras.map((b) => <Cell key={b.name} fill={(ESTADOS[b.estado] || ESTADOS.sin_datos).color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-start">
        {/* Lectura del tablero */}
        <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-sm font-bold text-slate-700 mb-2">Lectura del tablero</h3>
          <ul className="space-y-1.5">
            {LECTURA.map(([estado, texto]) => (
              <li key={estado} className="text-xs text-slate-600 flex items-start gap-2">
                <span className="mt-0.5 inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: ESTADOS[estado].color }} />
                <span><span className="font-semibold text-slate-700">{ESTADOS[estado].label}:</span> {texto}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-slate-500 mt-3">
            Bono: menos de 90% sin bono · 90 a 95.9% 50% · 96 a 100% 100% · más de 100% 110%.
          </p>
        </div>

        {/* Distribución del estado */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-sm font-bold text-slate-700 mb-2">Distribución del estado</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
            <table className="w-full text-left" data-testid="dash-conteo">
              <thead>
                <tr className="text-[11px] uppercase text-slate-400 border-b border-slate-100">
                  <th className="py-1.5 px-2">Estado</th>
                  <th className="py-1.5 px-2 text-right">Áreas</th>
                </tr>
              </thead>
              <tbody>
                {ORDEN_ESTADOS.map((e) => (
                  <tr key={e} className="border-b border-slate-50">
                    <td className="py-1.5 px-2 text-xs text-slate-600">
                      <span className="inline-block w-2.5 h-2.5 rounded-full mr-2 align-middle" style={{ backgroundColor: ESTADOS[e].color }} />
                      {ESTADOS[e].label}
                    </td>
                    <td className="py-1.5 px-2 text-sm font-semibold text-slate-800 text-right">{conteo[e] || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={dona} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                  {dona.map((d) => <Cell key={d.estado} fill={ESTADOS[d.estado].color} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
